import React from "react";
import { Modal, Button, Form, Row, Col, Spinner, ButtonGroup } from "react-bootstrap";
import Select from "react-select";
import "../../styles/duplication-modal.css";

const SalaireDuplicationModal = ({
  show,
  onHide,
  onSubmit,
  duplicating,
  sourceEmploye,
  sourceMois,
  sourceAnnee,
  moisOptions,
  annees,
  contrats,
  dupYear,
  setDupYear,
  dupMonth,
  setDupMonth,
  dupMonths,
  setDupMonths,
  dupMultiMonths,
  setDupMultiMonths,
  dupTargets = [],
  onAddTargets,
  onRemoveTarget,
  onClearTargets,
  enableDupTargets,
  setEnableDupTargets,
  selectedTargetEmployeeIds,
  setSelectedTargetEmployeeIds,
}) => {
  const ORDRE_MOIS = {
    Janvier: 1, Février: 2, Mars: 3, Avril: 4, Mai: 5, Juin: 6,
    Juillet: 7, Août: 8, Septembre: 9, Octobre: 10, Novembre: 11, Décembre: 12,
  };
  const targetsByYear = dupTargets.reduce((acc, t) => {
    (acc[t.annee] = acc[t.annee] || []).push(t.mois);
    return acc;
  }, {});
  Object.keys(targetsByYear).forEach((y) =>
    targetsByYear[y].sort((a, b) => (ORDRE_MOIS[a] || 13) - (ORDRE_MOIS[b] || 13))
  );
  const canAdd = dupYear && (dupMultiMonths ? dupMonths.length > 0 : !!dupMonth);
  const toggleMonth = (mois) => {
    if (mois === sourceMois) return;
    setDupMonths((prev) =>
      prev.includes(mois) ? prev.filter((m) => m !== mois) : [...prev, mois]
    );
  };

  const selectAllMonths = () => {
    setDupMonths(moisOptions.filter((m) => m !== sourceMois));
  };

  const clearAllMonths = () => {
    setDupMonths([]);
  };

  const handleModeChange = (multi) => {
    setDupMultiMonths(multi);
    if (multi) {
      setDupMonth("");
    } else {
      setDupMonths([]);
    }
  };

  const currentCount = dupMultiMonths ? dupMonths.length : dupMonth ? 1 : 0;
  const targetCount = dupTargets.length + currentCount;
  const yearsCount = new Set([
    ...dupTargets.map((t) => t.annee),
    ...(currentCount > 0 && dupYear ? [parseInt(dupYear)] : []),
  ]).size;
  const employeeCount = enableDupTargets ? selectedTargetEmployeeIds.length : 1;

  const summaryParts = [];
  if (sourceMois && sourceAnnee) {
    summaryParts.push(`depuis ${sourceMois} ${sourceAnnee}`);
  }
  if (targetCount > 0) {
    summaryParts.push(`vers ${targetCount} mois sur ${yearsCount} année${yearsCount > 1 ? "s" : ""}`);
  }
  if (enableDupTargets && selectedTargetEmployeeIds.length > 0) {
    summaryParts.push(`pour ${selectedTargetEmployeeIds.length} employé(s)`);
  }

  const canSubmit =
    dupTargets.length > 0 ||
    (dupYear && ((dupMultiMonths && dupMonths.length > 0) || (!dupMultiMonths && dupMonth)));

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="duplication-modal"
      backdrop="static"
    >
      <Modal.Header closeButton className="duplication-modal-header">
        <div>
          <Modal.Title>Dupliquer les salaires</Modal.Title>
          <div className="duplication-subtitle">
            Copier les lignes de salaire vers une ou plusieurs périodes cibles
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="duplication-modal-body">
        <div className="duplication-source-card">
          <div className="label">Période source</div>
          <div className="value">
            {sourceEmploye || "Employé"} — {sourceMois} {sourceAnnee}
          </div>
        </div>

        <div className="duplication-section">
          <div className="duplication-section-title">
            <span>📅</span> Période(s) cible(s)
          </div>

          <Row className="g-3 mb-3">
            <Col md={5}>
              <Form.Label className="fw-semibold small text-secondary">
                Année cible
              </Form.Label>
              <Form.Select
                className="duplication-year-select"
                value={dupYear}
                onChange={(e) => setDupYear(e.target.value)}
              >
                <option value="">Choisir une année</option>
                {annees.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={7} className="d-flex flex-column justify-content-end">
              <Form.Label className="fw-semibold small text-secondary mb-2">
                Mode de sélection
              </Form.Label>
              <ButtonGroup className="duplication-mode-toggle w-100">
                <Button
                  variant={!dupMultiMonths ? "success" : "outline-secondary"}
                  onClick={() => handleModeChange(false)}
                  active={!dupMultiMonths}
                >
                  Un seul mois
                </Button>
                <Button
                  variant={dupMultiMonths ? "success" : "outline-secondary"}
                  onClick={() => handleModeChange(true)}
                  active={dupMultiMonths}
                >
                  Plusieurs mois
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          {dupMultiMonths ? (
            <>
              <div className="duplication-months-toolbar">
                <span className="selected-count">
                  {dupMonths.length} mois sélectionné{dupMonths.length !== 1 ? "s" : ""}
                </span>
                <div className="d-flex gap-3">
                  <Button
                    variant="link"
                    className="btn-link-sm text-success p-0"
                    onClick={selectAllMonths}
                  >
                    Tout sélectionner
                  </Button>
                  <Button
                    variant="link"
                    className="btn-link-sm text-secondary p-0"
                    onClick={clearAllMonths}
                  >
                    Tout effacer
                  </Button>
                </div>
              </div>
              <div className="duplication-months-grid">
                {moisOptions.map((m) => {
                  const isSource = m === sourceMois;
                  const selected = dupMonths.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      disabled={isSource}
                      className={`duplication-month-chip ${selected ? "selected" : ""} ${
                        isSource ? "source-month" : ""
                      }`}
                      onClick={() => toggleMonth(m)}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              <small className="text-muted d-block mt-2">
                Cliquez sur les mois pour les ajouter ou retirer. Le mois source est
                exclu.
              </small>
            </>
          ) : (
            <div className="duplication-months-grid">
              {moisOptions.map((m) => {
                const isSource = m === sourceMois;
                const selected = dupMonth === m;
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={isSource}
                    className={`duplication-month-chip ${selected ? "selected" : ""} ${
                      isSource ? "source-month" : ""
                    }`}
                    onClick={() => setDupMonth(m)}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button variant="outline-success" size="sm" onClick={onAddTargets} disabled={!canAdd}>
              ➕ Ajouter ces mois à la liste
            </Button>
          </div>
          <small className="text-muted d-block mt-2">
            Astuce : choisissez une année, sélectionnez ses mois, puis « Ajouter à la liste ».
            Répétez pour une autre année afin de dupliquer sur plusieurs années.
          </small>
        </div>

        {dupTargets.length > 0 && (
          <div className="duplication-section">
            <div className="duplication-section-title d-flex align-items-center">
              <span>🧺</span> Périodes sélectionnées ({dupTargets.length})
              <Button variant="link" className="btn-link-sm text-secondary p-0 ms-auto" onClick={onClearTargets}>
                Tout effacer
              </Button>
            </div>
            {Object.keys(targetsByYear).sort((a, b) => Number(a) - Number(b)).map((year) => (
              <div key={year} className="mb-2">
                <div className="fw-semibold small text-secondary mb-1">{year}</div>
                <div className="d-flex flex-wrap gap-2">
                  {targetsByYear[year].map((m) => (
                    <span
                      key={`${year}-${m}`}
                      className="duplication-month-chip selected"
                      style={{ cursor: "default", display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      {m}
                      <button
                        type="button"
                        onClick={() => onRemoveTarget(Number(year), m)}
                        style={{ border: "none", background: "transparent", color: "inherit", fontWeight: 700, cursor: "pointer", lineHeight: 1 }}
                        title="Retirer"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="duplication-section duplication-employees-section">
          <Form.Check
            type="switch"
            id="dup-targets-switch"
            className="mb-3"
            label="Dupliquer vers d'autres employés"
            checked={enableDupTargets}
            onChange={(e) => setEnableDupTargets(e.target.checked)}
          />
          {enableDupTargets && (
            <Form.Group>
              <Form.Label className="fw-semibold small text-secondary">
                Employés cibles
              </Form.Label>
              <Select
                isMulti
                options={contrats.map((c) => ({
                  value: c.id_employe,
                  label: `${c.nom_employe} ${c.prenom_employe} | ${c.poste}`,
                }))}
                value={selectedTargetEmployeeIds
                  .map((id) => {
                    const ctr = contrats.find((c) => c.id_employe === id);
                    return ctr
                      ? {
                          value: id,
                          label: `${ctr.nom_employe} ${ctr.prenom_employe} | ${ctr.poste}`,
                        }
                      : null;
                  })
                  .filter(Boolean)}
                onChange={(vals) =>
                  setSelectedTargetEmployeeIds((vals || []).map((v) => v.value))
                }
                placeholder="Rechercher et sélectionner des employés…"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 44,
                    borderRadius: 8,
                    borderColor: "#cbd5e1",
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Form.Group>
          )}
        </div>

        {summaryParts.length > 0 && (
          <div className="duplication-summary">
            <strong>Récapitulatif :</strong> Duplication {summaryParts.join(" ")}
            {targetCount > 0 && employeeCount > 0 && (
              <span>
                {" "}
                — environ {targetCount * employeeCount} opération
                {targetCount * employeeCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="duplication-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={duplicating}>
          Annuler
        </Button>
        <Button variant="success" onClick={onSubmit} disabled={duplicating || !canSubmit}>
          {duplicating ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Duplication…
            </>
          ) : (
            <>Lancer la duplication</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SalaireDuplicationModal;
